<script setup lang="ts">
import Logo from '@/assets/images/logoBlack.png';
import { computed, ref } from 'vue';
import { MEDIA_QUERY_MOBILE } from '@/shared/constants/mediaQuery';
import { useWindowSize } from '@vueuse/core';
import { useRouter } from 'vue-router';

const { width } = useWindowSize();
const router = useRouter();

const routes = [
	{
		name: 'home',
		path: '/',
		label: 'HOME',
	},
	{
		name: 'info',
		path: '/info',
		label: 'CHI SIAMO',
	},
	{
		name: 'associate',
		path: '/associate',
		label: 'DIVENTA SOCIO',
	},
	{
		name: 'aboutUs',
		path: '/about-us',
		label: 'DICONO DI NOI',
	},
	{
		name: 'contacts',
		path: '/contacts',
		label: 'CONTATTI',
	},
];

const isMobile = computed(() => width.value <= MEDIA_QUERY_MOBILE);
const isMenuOpen = ref(false);

const isRouteActive = (name: string) => {
	return name === router.currentRoute.value.name;
};

const goToHome = () => {
	router.push({ name: 'home' });
};
</script>

<template>
	<nav class="gaf-header">
		<div class="gaf-header__wrapper">
			<div class="gaf-header__navigation">
				<div class="gaf-header__navigation__logo">
					<img :src="Logo" alt="logo" @click="goToHome" />
				</div>
				<div v-if="!isMobile" class="gaf-header__navigation__items">
					<router-link
						v-for="link in routes"
						:key="link.name"
						class="gaf-header__navigation__items__link"
						:class="{ 'gaf-header__navigation__items__link--active': isRouteActive(link.name) }"
						:to="link.path"
					>
						{{ link.label }}
					</router-link>
				</div>
				<button v-else class="gaf-header__navigation__mobile-menu-btn" @click="isMenuOpen = !isMenuOpen">
					<!-- Mobile navigation (e.g., hamburger menu) can be implemented here -->
					<i v-if="!isMenuOpen" class="pi pi-bars" style="font-size: 1.2rem" aria-label="Toggle menu" />
					<i v-else class="pi pi-times" style="font-size: 1.2rem" aria-label="Toggle menu" />
				</button>
			</div>
			<div v-if="isMenuOpen && isMobile" class="gaf-header__navigation--mobile">
				<router-link
					v-for="link in routes"
					:key="link.name"
					class="gaf-header__navigation__items__link"
					:class="{ 'gaf-header__navigation__items__link--active': isRouteActive(link.name) }"
					:to="link.path"
					@click="isMenuOpen = false"
				>
					{{ link.label }}
				</router-link>
			</div>
		</div>
	</nav>
</template>

<style scoped lang="scss">
.gaf-header {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 50;
	background: rgba($background-light, 0.98);
	backdrop-filter: blur(12px);
	border-bottom: 1px solid var(--border);
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);

	&__wrapper {
		padding: $spacing-L;
	}

	&__navigation {
		display: flex;
		gap: 40px;
		align-items: center;
		justify-content: space-between;

		&__logo {
			cursor: pointer;
			img {
				height: 100%;
				width: 60px;
				transition: transform 0.3s ease;
			}

			&:hover img {
				transform: scale(1.1);
			}
		}

		&__items {
			color: var(--foreground);
			display: flex;
			gap: 40px;

			&__link {
				@include font-body-small--bold;
				color: $foreground-light;
				text-decoration: none;
				position: relative;
				transition: color 0.3s ease;

				&::after {
					content: '';
					position: absolute;
					width: 100%;
					height: 2px;
					bottom: -8px;
					left: 0;
					background: $accent-light;
					transform: scaleX(0);
					transform-origin: center;
					transition: transform 0.3s ease;
				}

				&:not(&--active):hover {
					color: $accent-light;

					&::after {
						transform: scaleX(1);
					}
				}

				&--active {
					color: $primary-light;
				}
			}
		}

		&__mobile-menu-btn {
			display: block;
			background: none;
			border: none;
			color: $foreground-light;
			padding: $spacing-XS;
			cursor: pointer;
		}

		&--mobile {
			display: block;
			margin-top: 1rem;
			padding-bottom: 1rem;

			.gaf-header__navigation__items__link {
				display: block;
				@include font-body-small--bold;
				color: $foreground-light;
				text-decoration: none;
				padding: 1rem 0;
				transition: color 0.3s ease;

				&:not(&--active):hover {
					color: $accent-light;
				}

				&--active {
					color: $primary-light;
				}
			}
		}
	}
}
</style>
